import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Mail, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { currentMembership, useCanEdit, useDojos, useIsAdmin, useMyInstructing, type Membership } from '@/lib/queries';
import { adminLinks, instructorLinks } from '@/lib/links';
import { composeLink, dojoComposeLink, useEmailBatches } from '@/lib/email';
import { useAuth } from '@/lib/auth';
import { ageBandLabel, date, dateTime, gradeLabel } from '@/lib/format';
import { Avatar } from '@/components/Photo';
import { checkState, CheckDate, declarationDue } from '@/components/admin/Instructors';
import { ErrorNote, Spinner } from '@/components/Layout';

interface Candidate {
  id: string; for_rank: number; from_rank: number | null; result: string; recommended: boolean | null; recommended_by: string | null; recommendation_note: string | null; previously_failed: boolean | null; graded_on: string;
  person: { id: string; first_name: string; last_name: string; photo_path: string | null; home_dojo_id: string | null; date_of_birth: string | null } | null;
  event: { id: string; title: string; slug: string; starts_at: string } | null;
}
interface Student { id: string; first_name: string; last_name: string; grade_rank: number | null; photo_path: string | null; date_of_birth: string | null; frozen_at: string | null; memberships: Membership[] }

/**
 * For instructors: sign off gradings for their dojo's students, and see who is (and isn't) a current member.
 * Admins can open the same screen for any dojo at /dojo/:dojoId and do everything an instructor can.
 */
export function MyDojo() {
  const { dojoId } = useParams();
  const isAdmin = useIsAdmin();
  const admin = isAdmin.data === true;
  const dojos = useDojos();
  const mine = useMyInstructing();
  const actingFor = dojoId && admin ? (dojos.data ?? []).find(d => d.id === dojoId) : undefined;
  const canEdit = useCanEdit();
  const { session } = useAuth();
  const sentByMe = useEmailBatches({ mine: session?.user.id });
  const instructing = dojoId ? { isLoading: isAdmin.isLoading || dojos.isLoading, data: actingFor ? [{ id: actingFor.id, role: 'admin', dbs_expires_on: null, insurance_expires_on: null, first_aid_expires_on: null, declaration_signed_on: null, dojo: actingFor }] : [] } : mine;
  // Admins open the full admin record; other instructors the student's page.
  const personName = (p: { id: string; first_name: string; last_name: string } | null) => !p ? null
    : <Link to={admin ? adminLinks.person(p.id) : instructorLinks.student(p.id)} className="link">{p.first_name} {p.last_name}</Link>;
  const eventLink = (e: Candidate['event']) => e && <Link to={`/events/${e.slug}`} className="link">{e.title}</Link>;
  const qc = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const dojoIds = (instructing.data ?? []).map(i => i.dojo?.id).filter(Boolean) as string[];

  const candidates = useQuery({
    queryKey: ['dojo-candidates', dojoIds.join()],
    enabled: dojoIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('grading').select('id,for_rank,from_rank,result,recommended,recommended_by,recommendation_note,previously_failed,graded_on,person:person_id!inner(id,first_name,last_name,photo_path,home_dojo_id,date_of_birth),event:event_id(id,title,slug,starts_at)')
        .eq('result', 'registered').in('person.home_dojo_id', dojoIds).gte('graded_on', new Date().toISOString().slice(0, 10)).order('graded_on');
      if (error) throw error;
      return data as unknown as Candidate[];
    },
  });
  const students = useQuery({
    queryKey: ['dojo-students', dojoIds.join()],
    enabled: dojoIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('person').select('id,first_name,last_name,grade_rank,photo_path,date_of_birth,frozen_at,memberships:membership(id,starts_on,ends_on,status,plan:plan_id(name))')
        .in('home_dojo_id', dojoIds).eq('status', 'active').order('last_name');
      if (error) throw error;
      return data as unknown as Student[];
    },
  });

  // An admin viewer who also instructs keeps their instructor rights for their own dojos; elsewhere they only look.
  const ownDojo = (mine.data ?? []).some(i => i.dojo?.id && dojoIds.includes(i.dojo.id));
  const canSignOff = !actingFor || canEdit || ownDojo;
  // Instructors email their own dojo from here; an admin looking at another dojo uses the admin email screen; viewers do neither.
  const emailLink = (a: { kind: 'dojo' | 'person'; id: string }) => !actingFor || ownDojo ? dojoComposeLink(a) : canEdit ? composeLink(a) : null;

  // A search result links to #s-<student>: scroll to that card once the students have loaded.
  const { hash } = useLocation();
  useEffect(() => { if (students.data && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }); }, [students.data, hash]);
  if (instructing.isLoading) return <Spinner />;
  if (!dojoIds.length) return <Navigate to="/home" replace />;

  async function decide(id: string, yes: boolean) {
    setError('');
    const { error } = await supabase.rpc('recommend_grading', { p_grading: id, p_recommend: yes, p_note: notes[id] ?? null });
    if (error) { setError('Could not save that. Please try again.'); return; }
    api('/gradings/notify', { method: 'POST', body: {} }).catch(() => {});  // tell the household now
    await qc.invalidateQueries({ queryKey: ['dojo-candidates'] });
  }

  const today = new Date().toISOString().slice(0, 10);
  const lapsed = (students.data ?? []).filter(s => { const m = currentMembership(s as never); return !m || m.status !== 'active' || m.ends_on < today; });
  const waiting = (candidates.data ?? []).filter(c => c.recommended === null);
  const decided = (candidates.data ?? []).filter(c => c.recommended !== null);

  return (
    <div className="space-y-8">
      <div>
        <p className="eyebrow">{actingFor ? 'Instructor view · acting as admin' : 'Instructor'}</p>
        <h1 className="mt-1 text-3xl">{instructing.data!.map((i, n) => <span key={i.id}>{n > 0 && ' and '}{admin && i.dojo ? <Link to={adminLinks.dojo(i.dojo.id)} className="hover:underline">{i.dojo.short_name ?? i.dojo.name}</Link> : i.dojo?.short_name ?? i.dojo?.name}</span>)}</h1>
        {actingFor ? <p className="mt-2 text-[15px] text-ink-500">{ownDojo ? 'You instruct here, so sign-offs are recorded in your name.' : canSignOff ? "You can do anything this dojo's instructors can. Sign-offs are recorded as made by an admin." : "View only. Sign-offs are made by this dojo's instructors or an admin."}</p> : (() => {
          // Their own DBS and insurance, from the first dojo row (the dates are per instructor role, normally the same).
          const me = instructing.data![0]!;
          const due = [me.dbs_expires_on, me.insurance_expires_on, me.first_aid_expires_on, declarationDue(me.declaration_signed_on)].some(d => checkState(d) !== 'ok');
          return <p className={`mt-2 text-[15px] ${due ? '' : 'text-ink-500'}`}>Your DBS: <CheckDate on={me.dbs_expires_on} /> · Insurance: <CheckDate on={me.insurance_expires_on} /> · First aid: <CheckDate on={me.first_aid_expires_on} /> · Declaration: <CheckDate on={declarationDue(me.declaration_signed_on)} />{due && <span className="block text-sm text-ink-500">Send renewed details to the Membership Secretary.</span>}</p>;
        })()}
      </div>

      <section className="space-y-3">
        <h2 className="text-2xl">Gradings to sign off {waiting.length > 0 && <span className="pill ml-2 bg-brand-600 align-middle text-white">{waiting.length}</span>}</h2>
        {error && <ErrorNote>{error}</ErrorNote>}
        {!waiting.length && <p className="card text-[15px] text-ink-500">Nothing waiting. We'll email you when a student enters a grading.</p>}
        {waiting.map(c => (
          <div key={c.id} className="card flex flex-wrap items-start gap-4">
            <Avatar path={c.person?.photo_path} name={`${c.person?.first_name} ${c.person?.last_name}`} size={52} />
            <div className="min-w-[200px] flex-1">
              <p className="text-lg font-bold">{personName(c.person)}{ageBandLabel(c.person?.date_of_birth, c.event?.starts_at) && <span className="font-normal text-ink-500"> · {ageBandLabel(c.person?.date_of_birth, c.event?.starts_at)}</span>}</p>
              <p className="text-[15px] text-ink-700">{gradeLabel(c.from_rank)} → <strong>{gradeLabel(c.for_rank)}</strong>{c.event && <> at <Link to={`/events/${c.event.slug}`} className="link">{c.event.title}</Link>, {dateTime(c.event.starts_at)}</>}</p>
              {c.previously_failed && <p className="text-sm text-ink-500">Has attempted this grade before.</p>}
              {canSignOff && <label className="mt-2 block"><span className="sr-only">Note to the student (optional)</span>
                <input className="input" placeholder="Note to the student (optional)" value={notes[c.id] ?? ''} onChange={e => setNotes({ ...notes, [c.id]: e.target.value })} /></label>}
            </div>
            {canSignOff ? <div className="flex gap-2 self-center">
              <button className="btn-primary" onClick={() => decide(c.id, true)}><Check size={16} /> Approve</button>
              <button className="btn border border-ink-900 text-ink-900 hover:bg-stone-100" onClick={() => decide(c.id, false)}><X size={16} /> Not yet</button>
            </div> : <span className="self-center text-sm text-ink-500">Waiting for the instructor</span>}
          </div>
        ))}
        {decided.length > 0 && (
          <ul className="card divide-y divide-stone-100 p-0 text-[15px]">
            {decided.map(c => <li key={c.id} className="flex flex-wrap justify-between gap-2 px-5 py-3"><span>{personName(c.person)}{ageBandLabel(c.person?.date_of_birth, c.event?.starts_at) && <span className="text-ink-500"> ({ageBandLabel(c.person?.date_of_birth, c.event?.starts_at)})</span>} · {gradeLabel(c.for_rank)} · {eventLink(c.event)}</span><span className={c.recommended ? 'font-semibold' : 'text-brand-700'}>{c.recommended ? `Approved by ${c.recommended_by}` : 'Not approved'}</span></li>)}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-2xl">Students</h2>
          {dojoIds.map(id => { const l = emailLink({ kind: 'dojo', id }); const d = instructing.data!.find(i => i.dojo?.id === id)?.dojo; return l && <Link key={id} {...l} className="btn-ghost border border-stone-300 text-sm"><Mail size={14} /> Email {dojoIds.length > 1 ? `everyone at ${d?.short_name ?? d?.name}` : 'everyone'}</Link>; })}
        </div>
        {lapsed.length > 0 && <p className="text-[15px] text-brand-700">{lapsed.length} {lapsed.length === 1 ? 'student is' : 'students are'} not currently a member and so not insured to train.</p>}
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(students.data ?? []).map(s => {
            const m = currentMembership(s as never);
            const ok = !!m && m.status === 'active' && m.ends_on >= today;
            return (
              <li key={s.id} id={`s-${s.id}`} className="card flex scroll-mt-4 items-center gap-3 py-3 target:ring-2 target:ring-brand-200">
                <Avatar path={s.photo_path} name={`${s.first_name} ${s.last_name}`} size={40} />
                <div className="min-w-0">
                  <p className="font-bold">{personName(s)}{(() => { const l = emailLink({ kind: 'person', id: s.id }); return l && <Link {...l} className="ml-2 inline-block align-middle text-ink-500 hover:text-ink-900" aria-label={`Email ${s.first_name}`}><Mail size={14} /></Link>; })()}</p>
                  <p className="text-sm text-ink-500">{ageBandLabel(s.date_of_birth) && <>{ageBandLabel(s.date_of_birth)} · </>}{gradeLabel(s.grade_rank)} · {s.frozen_at ? <span className="font-semibold text-indigo-600">membership paused</span> : <span className={ok ? '' : 'font-semibold text-brand-700'}>{ok ? `member to ${date(m!.ends_on)}` : m ? `ended ${date(m.ends_on)}` : 'not a member'}</span>}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {(sentByMe.data?.length ?? 0) > 0 && (
        <section className="space-y-3">
          <h2 className="text-2xl">Emails you have sent</h2>
          <ul className="card divide-y divide-stone-100 p-0 text-[15px]">
            {sentByMe.data!.slice(0, 20).map(b => (
              <li key={b.id} className="flex flex-wrap justify-between gap-2 px-5 py-3">
                <span><Link to={`/dojo/email/sent/${b.id}`} className="link">{b.subject}</Link> <span className="text-ink-500">to {b.audience_label}</span></span>
                <span className="text-ink-500">{dateTime(b.created_at)} · {b.sent_count} of {b.recipient_count} sent</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
